// DS dependency graph — symbol table + directed dependency edges
// Built from DS files during content creation
import { DsFile } from 'dragonmouse-shared/interfaces';

export type SymbolKind = 'var' | 'component' | 'class';

// Symbol table entry: what we know about each defined name
export interface DsDecl {
	kind: SymbolKind;
	file: string;
}

export interface DsDepGraph {
	symbols: Record<string, DsDecl>;
	edges: [string, string][];
	usedBy: Record<string, string[]>;
	uses: Record<string, string[]>;
	brokenRefs: [string, string][];
	orphans: string[];
}

// Regex patterns for extraction
const VAR_REF_RE = /var\(\s*(--(theme|ds)-[\w-]+)[^)]*\)/g;
const COMPONENT_DEF_RE = /customElements\.define\(\s*['"]([^'"]+)['"]/g;
const COMPONENT_REF_RE = /<(ds-[a-z][a-z0-9-]*)/g;

// Resolves a component tag name from a file path like "components/button/button.css"
function componentTagFromPath(path: string): string | null {
	const match = path.match(/^components\/([\w-]+)\//);
	return match ? `ds-${match[1]}` : null;
}

// Extracts all var() references from a string
function extractVarRefs(content: string): string[] {
	const refs: string[] = [];
	let m: RegExpExecArray | null;
	const re = new RegExp(VAR_REF_RE.source, 'g');
	while ((m = re.exec(content)) !== null) refs.push(m[1]);
	return refs;
}

// Extracts symbols and edges from a CSS file that defines vars (theme, tokens)
function extractCssVarFile(
	file: DsFile,
	kind: SymbolKind,
	symbols: Record<string, DsDecl>,
	edges: [string, string][]
): void {
	const lines = file.content.split('\n');
	for (const line of lines) {
		const defMatch = /(--(theme|ds)-[\w-]+)\s*:(.+)/.exec(line);
		if (!defMatch) continue;
		const name = defMatch[1];
		const value = defMatch[3];
		if (!(name in symbols)) {
			symbols[name] = { kind, file: file.path };
		}
		for (const ref of extractVarRefs(value)) {
			edges.push([name, ref]);
		}
	}
}

// Extracts symbols and edges from a recipe CSS file (class definitions)
function extractRecipeCss(
	file: DsFile,
	symbols: Record<string, DsDecl>,
	edges: [string, string][]
): void {
	const lines = file.content.split('\n');
	let currentClass: string | null = null;
	let depth = 0;

	for (const line of lines) {
		const trimmed = line.trim();

		if (currentClass && depth > 0) {
			for (const ref of extractVarRefs(trimmed)) {
				edges.push([currentClass, ref]);
			}
			if (trimmed.includes('}')) {
				depth--;
				if (depth === 0) currentClass = null;
			}
			continue;
		}

		const classMatch = /^\.(ds-[\w-]+)\s*\{/.exec(trimmed);
		if (classMatch) {
			currentClass = classMatch[1];
			if (!(currentClass in symbols)) {
				symbols[currentClass] = { kind: 'class', file: file.path };
			}
			depth = 1;
			if (trimmed.includes('}')) {
				depth = 0;
				currentClass = null;
			}
		}
	}
}

// All var() refs attributed to the component derived from file path
function extractComponentCss(
	file: DsFile,
	symbols: Record<string, DsDecl>,
	edges: [string, string][]
): void {
	const tag = componentTagFromPath(file.path);
	if (!tag) return;
	if (!(tag in symbols)) {
		symbols[tag] = { kind: 'component', file: file.path };
	}
	for (const ref of extractVarRefs(file.content)) {
		edges.push([tag, ref]);
	}
}

function extractComponentJs(
	file: DsFile,
	symbols: Record<string, DsDecl>,
	edges: [string, string][]
): void {
	const re = new RegExp(COMPONENT_DEF_RE.source, 'g');
	let m: RegExpExecArray | null;
	const definedTags: string[] = [];

	while ((m = re.exec(file.content)) !== null) {
		const tag = m[1];
		if (!(tag in symbols)) {
			symbols[tag] = { kind: 'component', file: file.path };
		}
		definedTags.push(tag);
	}

	const source = definedTags[0] || componentTagFromPath(file.path);
	if (!source) return;

	for (const ref of extractVarRefs(file.content)) {
		edges.push([source, ref]);
	}

	const compRe = new RegExp(COMPONENT_REF_RE.source, 'g');
	while ((m = compRe.exec(file.content)) !== null) {
		const refTag = m[1];
		if (refTag !== source) {
			edges.push([source, refTag]);
		}
	}
}

// Leaf consumer — uses file path as source
function extractGalleryHtml(file: DsFile, edges: [string, string][]): void {
	const source = file.path;

	for (const ref of extractVarRefs(file.content)) {
		edges.push([source, ref]);
	}

	const re = new RegExp(COMPONENT_REF_RE.source, 'g');
	let m: RegExpExecArray | null;
	while ((m = re.exec(file.content)) !== null) {
		edges.push([source, m[1]]);
	}
}

export function buildDependencyGraph(files: DsFile[]): DsDepGraph {
	const symbols: Record<string, DsDecl> = {};
	const edges: [string, string][] = [];

	for (const file of files) {
		const p = file.path;

		if (p.startsWith('theme/') && p.endsWith('.css')) {
			extractCssVarFile(file, 'var', symbols, edges);
		} else if (p.startsWith('styles/tokens') && p.endsWith('.css')) {
			extractCssVarFile(file, 'var', symbols, edges);
		} else if (p.startsWith('styles/recipes/') && p.endsWith('.css')) {
			extractRecipeCss(file, symbols, edges);
		} else if (p.startsWith('styles/_internal/') && p.endsWith('.css')) {
			for (const ref of extractVarRefs(file.content)) {
				edges.push([p, ref]);
			}
		} else if (p.startsWith('components/') && p.endsWith('.css')) {
			extractComponentCss(file, symbols, edges);
		} else if (p.startsWith('components/') && p.endsWith('.js')) {
			extractComponentJs(file, symbols, edges);
		} else if (p.startsWith('gallery/') && p.endsWith('.html')) {
			extractGalleryHtml(file, edges);
		}
	}

	// Build indices
	const usedBy: Record<string, string[]> = {};
	const uses: Record<string, string[]> = {};
	const brokenRefs: [string, string][] = [];

	for (const [source, target] of edges) {
		if (!(target in symbols)) {
			brokenRefs.push([source, target]);
		}
		if (!usedBy[target]) usedBy[target] = [];
		usedBy[target].push(source);
		if (!uses[source]) uses[source] = [];
		uses[source].push(target);
	}

	const orphans = Object.keys(symbols).filter(
		(name) => !usedBy[name] || usedBy[name].length === 0
	);

	return { symbols, edges, usedBy, uses, brokenRefs, orphans };
}
